import { Alert, Button, Step, StepButton, Stepper, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Carrier from "./Carrier";
import CarrierRelease from "./CarrierRelease";
import Confirmation from "./Confirmation";
import DocuSign from "./DocuSign";
import Review from "./Review";

// #33 - a transferring carrier that has a release letter adds an optional
// per-carrier Carrier Release signing step.
const hasReleaseStep = (data) =>
  (data?.contracts || []).some(
    (c) => c.policyCreation === "transfer" && c.withRelease === true
  );

// #18/#33 - an existing-agent carrier request signs nothing beyond the optional
// per-carrier release; a new agent also signs the mandatory payment-based document
// (Individual/Corp/AOC). #105 - keyed on the server's effectiveRequestType (an
// imported, staff-marked, or previously-submitted agent is established; everyone
// else is new; the agent is never asked). declaredRequestType/requestType remain
// as fallbacks for wizard payloads stored before effectiveRequestType existed
// (a resumed signing rehydrates from that stored payload).
const isNewAgent = (data) =>
  (data?.effectiveRequestType || data?.declaredRequestType || data?.requestType) !==
  "existing-agent";

// #29 — identity/details/payment are collected once at onboarding (shown
// read-only on Review), so the flow begins at carrier selection. #11 — the
// payment DocuSign gates Review for new agents. #33 — the step list is derived
// from the agent's carriers, so the Carrier Release step appears only when needed.
export const buildSteps = (data) => {
  const defs = [{ label: "Contract Request", component: Carrier }];
  if (hasReleaseStep(data)) {
    defs.push({ label: "Carrier Release", component: CarrierRelease });
  }
  if (isNewAgent(data)) {
    defs.push({ label: "DocuSign", component: DocuSign });
  }
  defs.push({ label: "Review", component: Review });
  defs.push({ label: "Confirmation", component: Confirmation });
  return defs.map((d, i) => ({ ...d, step: i + 1 }));
};

// The 1-based index of a step (by label) for a given wizard payload, or 0.
export const stepIndexOf = (data, label) => {
  const found = buildSteps(data).find((s) => s.label === label);
  return found ? found.step : 0;
};

// Seed the flow from the logged-in user: prefer their linked Agent (CRM)
// record when present, otherwise their account fields. NPN comes from the
// linked agent; brand-new agents enter it on the Agent Info step.
export const seedFromUser = (u) => {
  if (!u) return {};
  const a = u.agent || {};
  return {
    npn: a.npn ?? "",
    agent: a.id,
    // #105 — the server's new-vs-established call. Drives whether the mandatory
    // payment DocuSign step shows, and seeds the submission's requestType (the
    // backend recomputes it authoritatively at submit).
    requestType: a.effectiveRequestType ?? (a.id ? "existing-agent" : ""),
    effectiveRequestType: a.effectiveRequestType ?? "",
    firstName: a.firstName ?? u.firstName ?? "",
    lastName: a.lastName ?? u.lastName ?? "",
    preferredName: a.preferredName ?? u.preferredName ?? "",
    email: a.email ?? u.email ?? "",
    phone: a.phone ?? u.phone ?? "",
    address: a.address ?? u.address ?? "",
    city: a.city ?? u.city ?? "",
    state: a.state ?? u.state ?? "",
    zip: a.zip ?? u.zip ?? "",
    // #22 — pre-select the agent's most recent FSD ("who recommended you?") pick.
    fsd: a.fsd ?? "",
    // #29 — payment info captured at onboarding; drives the #20 carrier column
    // and rides onto the submission (the backend also sources it from the agent).
    payment: a.payment ?? "",
    paymentCorporation: a.paymentCorporation ?? "",
    paymentNpn: a.paymentNpn ?? "",
    paymentTaxId: a.paymentTaxId ?? "",
    paymentUpline: a.paymentUpline ?? "",
  };
};

// #11 — when an agent returns from signing, the wizard is re-mounted fresh (a
// full-page navigation happened). The signing-return route rehydrates it from the
// server-stored payload, jumping straight to Review with the earlier steps marked
// done. initialData/initialStep/unsignedId carry that resumed state.
const Steps = ({ initialData = null, initialStep = 1, unsignedId = null }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [data, setData] = useState(() => ({
    ...seedFromUser(user),
    ...(initialData || {}),
    ...(unsignedId ? { unsignedId } : {}),
  }));
  // #33 - which steps are done, tracked by label (indices shift as the step list
  // changes). Steps before initialStep are pre-completed on a resume.
  const [completed, setCompleted] = useState(() => {
    const set = new Set();
    buildSteps({ ...seedFromUser(user), ...(initialData || {}) }).forEach((s) => {
      if (s.step < initialStep) set.add(s.label);
    });
    return set;
  });

  const steps = buildSteps(data).map((s) => ({
    ...s,
    completed: completed.has(s.label),
  }));
  const Content = (steps[step - 1] || steps[steps.length - 1]).component;

  // Keep the flow in sync with the user until it's actually underway. The user
  // can arrive or be re-pulled after mount (the dashboard/request-start refresh),
  // and the new-vs-existing signal it carries decides whether the onboarding
  // DocuSign step shows. Skip when resuming from a stored payload (initialData
  // wins). Only touch things before the first step is completed, so nothing the
  // agent has entered gets clobbered.
  useEffect(() => {
    if (initialData) return;
    setData((prev) => {
      if (Object.keys(prev).length === 0) return seedFromUser(user);
      if (step === 1 && completed.size === 0) {
        return { ...prev, effectiveRequestType: seedFromUser(user).effectiveRequestType };
      }
      return prev;
    });
  }, [user, initialData, step, completed]);

  const goBack = () => {
    setStep(step - 1);
  };

  const completeStep = (stepData) => {
    const newData = { ...data, ...stepData };
    setData(newData);

    // Mark the current step done, then advance to whatever now follows it (the
    // step list can change here, e.g. completing carriers inserts Carrier Release).
    const currentLabel = (steps[step - 1] || {}).label;
    setCompleted((prev) => new Set(prev).add(currentLabel));
    const nextSteps = buildSteps(newData);
    const currentIndex = nextSteps.findIndex((s) => s.label === currentLabel);
    setStep(currentIndex + 2);
  };

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/")}
        sx={{ mb: 1 }}
      >
        Back to dashboard
      </Button>
      <Typography
        variant="h4"
        sx={{ marginBottom: 2, marginTop: -4 }}
        textAlign={"center"}
      >
        Contract Request
      </Typography>
      {/* #57 - white glove: a principal is preparing this request for one of
          their agents. Rides the payload so it survives the signing round-trip. */}
      {data.onBehalfOfAgent && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are preparing this request on behalf of {data.firstName}{" "}
          {data.lastName}.
        </Alert>
      )}
      <Stepper nonLinear activeStep={step - 1} sx={{ paddingBottom: 4 }}>
        {steps.map((item, index) => {
          // Don't render the final Confirmation step in the stepper.
          if (index === steps.length - 1) {
            return "";
          }
          return (
            <Step key={item.step} completed={item.completed}>
              {item.completed && step !== steps.length ? (
                <StepButton
                  onClick={() => {
                    setStep(item.step);
                  }}
                  color="inherit"
                  sx={{ textDecoration: "underline" }}
                >
                  {item.label}
                </StepButton>
              ) : (
                <StepButton color="inherit">{item.label}</StepButton>
              )}
            </Step>
          );
        })}
      </Stepper>
      <Content
        user={user}
        steps={steps}
        data={data}
        completeStep={completeStep}
      />
      {step > 1 && step < steps.length && (
        <Button sx={{marginTop: '8px'}} variant="contained" color="secondary" onClick={goBack}>
          Back
        </Button>
      )}
    </>
  );
};

export default Steps;
